/**
 * The `FontSize` type defines the available size options for font styling in the application.
 * It supports three predefined sizes: `'large'`, `'medium'`, and `'small'`.
 * Components using this type can dynamically adjust font sizes based on this predefined set.
 */
export type FontSize = 'large' | 'medium' | 'small' | 'tiny';

/**
 * The `GetSizeParams` interface is used to pass size-related parameters to functions or components.
 * It optionally includes a `$size` property that corresponds to the `FontSize` type, allowing
 * components to adjust their size according to the specified font size.
 */
interface GetSizeParams {
    /**
     * The optional size parameter for controlling font size in components.
     * If provided, it adjusts the font size to one of the predefined values: `'large'`, `'medium'`, or `'small'`.
     * If not provided, the default size will be applied.
     */
    $size?: FontSize;
}

/**
 * Retrieves the font size value based on the provided size parameter.
 * This function takes in an object with a $size property and returns the corresponding font size value.
 * The font size values are determined based on the provided size parameter:
 * 'large': Returns '1.6rem'.
 * 'small': Returns '1.2rem'.
 * 'medium' (default): Returns '1.4rem'.
 *
 * @param props       The styled components props.
 * @param props.$size The size parameter used to determine the font size value.
 * @returns The font size value based on the provided size parameter.
 *
 * @example
 * ```tsx
 * const fontSize = getPSize({ $size: 'large' }); // Returns '1.6rem'
 * ```
 */
export const getPSize = ({$size}: GetSizeParams) => {
    switch ($size) {
        case 'large':
            return '1.6rem';
        case 'small':
            return '1.2rem';
        case 'tiny':
            return '1rem';
        case 'medium':
        case undefined:
        default:
            return '1.4rem';
    }
};

/**
 * Retrieves the line height value based on the provided size parameter.
 * This function takes in an object with a $size property and returns the corresponding line height value.
 * The line height values are determined based on the provided size parameter:
 * 'large': Returns '2.4rem'.
 * 'small': Returns '1.6rem'.
 * 'medium' (default): Returns '2rem'.
 *
 * @param props       The styled components props.
 * @param props.$size The size parameter used to determine the line height value.
 * @returns The line height value based on the provided size parameter.
 *
 * @example
 * ```tsx
 * const lineHeight = getPLineHeight({ $size: 'large' }); // Returns '2.4rem'
 * ```
 */
export const getPLineHeight = ({$size}: GetSizeParams) => {
    switch ($size) {
        case 'large':
            return '2.4rem';
        case 'small':
        case 'tiny':
            return '1.6rem';
        case 'medium':
        case undefined:
        default:
            return '2rem';
    }
};